import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Settings,
  MessageCircle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Send,
  Phone,
  Key,
  Shield,
  Loader2,
  AlertCircle,
  Bell,
  Smartphone
} from 'lucide-react';
import Layout from '../components/Layout';
import WhatsAppSetup from '../components/core/WhatsAppSetup';
import SecuritySettings from '../components/core/SecuritySettings';
import { toast } from 'sonner';
import { InstallAppSection } from '../components/InstallApp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { token } = useAuth();
  
  // WhatChimp configuration state
  const [whatChimpStatus, setWhatChimpStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // Form state
  const [apiToken, setApiToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [webhookToken, setWebhookToken] = useState('wekeza-smart-webhook-2026');
  
  // Test message state
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hello from Wekeza-Smart! This is a test message.');
  
  // Notification history
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const fetchWhatChimpStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    try {
      const response = await axios.get(`${API}/whatchimp/status`);
      setWhatChimpStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch WhatChimp status:', error);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setIsLoadingNotifications(true);
    try {
      const response = await axios.get(`${API}/alerts/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWhatChimpStatus();
    fetchNotifications();
  }, [fetchWhatChimpStatus, fetchNotifications]);

  const saveConfiguration = async () => {
    if (!apiToken || !phoneNumberId) {
      toast.error(t('Please fill in all required fields', 'Tafadhali jaza sehemu zote zinazohitajika'));
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/whatchimp/config`, {
        api_token: apiToken,
        phone_number_id: phoneNumberId,
        webhook_verify_token: webhookToken
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('WhatChimp configuration saved!', 'Usanidi wa WhatChimp umehifadhiwa!'));
      fetchWhatChimpStatus();
      setApiToken('');
      setPhoneNumberId('');
    } catch (error) {
      toast.error(t('Failed to save configuration', 'Imeshindwa kuhifadhi usanidi'));
    } finally {
      setIsSaving(false);
    }
  };

  const sendTestMessage = async () => {
    if (!testPhone) {
      toast.error(t('Please enter a phone number', 'Tafadhali ingiza nambari ya simu'));
      return;
    }
    
    setIsTesting(true);
    try {
      const response = await axios.post(`${API}/whatchimp/test`, {
        recipient_phone: testPhone,
        message: testMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(t('Test message sent!', 'Ujumbe wa majaribio umetumwa!'));
      } else if (response.data.mock) {
        toast.info(t('Mock mode: ' + response.data.message, 'Hali ya majaribio: ' + response.data.message));
      } else {
        toast.error(response.data.error || t('Failed to send', 'Imeshindwa kutuma'));
      }
    } catch (error) {
      toast.error(t('Failed to send test message', 'Imeshindwa kutuma ujumbe wa majaribio'));
    } finally {
      setIsTesting(false);
    }
  };

  const checkAlerts = async () => {
    try {
      const response = await axios.post(`${API}/alerts/check-and-notify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { checked, triggered, notifications_sent } = response.data;
      toast.success(
        t(
          `Checked ${checked} alerts. ${triggered} triggered. ${notifications_sent} notifications sent.`,
          `Imeangalia arifa ${checked}. ${triggered} zimeshughulikiwa. Taarifa ${notifications_sent} zimetumwa.`
        )
      );
      fetchNotifications();
    } catch (error) {
      toast.error(t('Failed to check alerts', 'Imeshindwa kuangalia arifa'));
    }
  };

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {t('Settings', 'Mipangilio')}
            </h1>
            <p className="text-muted-foreground">
              {t('Configure WhatsApp integration and notifications', 'Sanidi muunganisho wa WhatsApp na taarifa')}
            </p>
          </div>
        </div>

        <Tabs defaultValue="ai-chatbot" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="app" data-testid="tab-app">
              <Smartphone className="w-4 h-4 mr-2" />
              {t('App', 'Programu')}
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="w-4 h-4 mr-2" />
              {t('Security', 'Usalama')}
            </TabsTrigger>
            <TabsTrigger value="ai-chatbot" data-testid="tab-ai-chatbot">
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('AI Chatbot', 'Chatbot ya AI')}
            </TabsTrigger>
            <TabsTrigger value="whatchimp" data-testid="tab-whatchimp">
              <Smartphone className="w-4 h-4 mr-2" />
              WhatChimp
            </TabsTrigger>
            <TabsTrigger value="test" data-testid="tab-test">
              <Send className="w-4 h-4 mr-2" />
              {t('Test', 'Jaribu')}
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-2" />
              {t('History', 'Historia')}
            </TabsTrigger>
          </TabsList>

          {/* App Install Tab */}
          <TabsContent value="app" className="space-y-4">
            <InstallAppSection language={t('en', 'sw') === 'sw' ? 'sw' : 'en'} />
            
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Smartphone className="w-5 h-5 text-emerald-400" />
                  {t('About the App', 'Kuhusu Programu')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">{t('Version', 'Toleo')}</p>
                    <p className="text-white font-medium">1.0.0</p>
                  </div>
                  <div>
                    <p className="text-gray-400">{t('Platform', 'Jukwaa')}</p>
                    <p className="text-white font-medium">PWA (Web App)</p>
                  </div>
                  <div>
                    <p className="text-gray-400">{t('Developer', 'Msanidi')}</p>
                    <p className="text-white font-medium">Wekeza-Smart</p>
                  </div>
                  <div>
                    <p className="text-gray-400">{t('Last Updated', 'Imesasishwa')}</p>
                    <p className="text-white font-medium">March 2026</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab - 2FA / Account Hardening */}
          <TabsContent value="security" className="space-y-4">
            <SecuritySettings />
          </TabsContent>


          {/* AI Chatbot Tab - Primary WhatsApp Integration */}
          <TabsContent value="ai-chatbot" className="space-y-4">
            <WhatsAppSetup />
          </TabsContent>

          {/* WhatChimp Configuration Tab */}
          <TabsContent value="whatchimp" className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>{t('Legacy Integration', 'Muunganisho wa Zamani')}:</strong> {t(
                  'WhatChimp is being replaced by the AI Chatbot (Twilio). Use the AI Chatbot tab for the recommended setup.',
                  'WhatChimp inabadilishwa na Chatbot ya AI (Twilio). Tumia kichupo cha Chatbot ya AI kwa usanidi unaopendekezwa.'
                )}
              </p>
            </div>
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#25D366]" />
                  {t('WhatChimp Status', 'Hali ya WhatChimp')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'WhatsApp Business API integration via WhatChimp',
                    'Muunganisho wa WhatsApp Business API kupitia WhatChimp'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingStatus ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('Loading...', 'Inapakia...')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      {whatChimpStatus?.is_configured ? (
                        <>
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                          <span className="font-medium text-green-600">
                            {t('Connected & Ready', 'Imeunganishwa na Tayari')}
                          </span>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Active
                          </Badge>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-6 h-6 text-amber-500" />
                          <span className="font-medium text-amber-600">
                            {t('Not Configured', 'Haijasanidiwa')}
                          </span>
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Setup Required
                          </Badge>
                        </>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span>API Token:</span>
                        {whatChimpStatus?.has_api_token ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>Phone ID:</span>
                        {whatChimpStatus?.has_phone_number_id ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Setup Instructions Card */}
            {!whatChimpStatus?.is_configured && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-500" />
                    {t('Setup Instructions', 'Maagizo ya Usanidi')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">1</span>
                      <div>
                        <span className="font-medium">{t('Sign up at WhatChimp', 'Jisajili WhatChimp')}</span>
                        <a 
                          href="https://whatchimp.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
                        >
                          whatchimp.com <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">2</span>
                      <span>{t('Complete Meta Business verification', 'Kamilisha uthibitishaji wa Meta Business')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">3</span>
                      <span>{t('Get API Token from Dashboard > Profile > API Developers', 'Pata API Token kutoka Dashibodi > Wasifu > API Developers')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">4</span>
                      <span>{t('Get Phone Number ID from your WhatsApp Business account', 'Pata Phone Number ID kutoka akaunti yako ya WhatsApp Business')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">5</span>
                      <span>{t('Create message templates (price_alert)', 'Tengeneza templates za ujumbe (price_alert)')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">6</span>
                      <span>{t('Enter your credentials below', 'Ingiza sifa zako hapa chini')}</span>
                    </li>
                  </ol>
                </CardContent>
              </Card>
            )}

            {/* Configuration Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('API Credentials', 'Sifa za API')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'Enter your WhatChimp API credentials to enable WhatsApp notifications',
                    'Ingiza sifa zako za API za WhatChimp kuwezesha taarifa za WhatsApp'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-token">{t('API Token', 'Tokeni ya API')}</Label>
                  <Input
                    id="api-token"
                    type="password"
                    placeholder="wc_xxxxxxxxxxxxxxxx"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    data-testid="whatchimp-api-token"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone-id">{t('Phone Number ID', 'Kitambulisho cha Nambari ya Simu')}</Label>
                  <Input
                    id="phone-id"
                    placeholder="1234567890123456"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                    data-testid="whatchimp-phone-id"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="webhook-token">{t('Webhook Verify Token', 'Tokeni ya Uthibitisho wa Webhook')}</Label>
                  <Input
                    id="webhook-token"
                    placeholder="wekeza-smart-webhook-2026"
                    value={webhookToken}
                    onChange={(e) => setWebhookToken(e.target.value)}
                    data-testid="whatchimp-webhook-token"
                  />
                </div>
                
                <Button 
                  onClick={saveConfiguration} 
                  disabled={isSaving}
                  className="w-full"
                  data-testid="save-whatchimp-config"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Saving...', 'Inahifadhi...')}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('Save Configuration', 'Hifadhi Usanidi')}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Message Tab */}
          <TabsContent value="test" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  {t('Send Test Message', 'Tuma Ujumbe wa Majaribio')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'Test your WhatChimp integration by sending a message',
                    'Jaribu muunganisho wako wa WhatChimp kwa kutuma ujumbe'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="test-phone">{t('Recipient Phone Number', 'Nambari ya Simu ya Mpokeaji')}</Label>
                  <Input
                    id="test-phone"
                    placeholder="+255712345678"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    data-testid="test-phone-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('Include country code (e.g., +255 for Tanzania)', 'Jumuisha msimbo wa nchi (mfano, +255 kwa Tanzania)')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="test-message">{t('Message', 'Ujumbe')}</Label>
                  <Input
                    id="test-message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    data-testid="test-message-input"
                  />
                </div>
                
                <Button 
                  onClick={sendTestMessage} 
                  disabled={isTesting}
                  className="w-full"
                  data-testid="send-test-btn"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Sending...', 'Inatuma...')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t('Send Test Message', 'Tuma Ujumbe wa Majaribio')}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Manual Alert Check */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {t('Check Price Alerts', 'Angalia Arifa za Bei')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'Manually check and trigger WhatsApp notifications for price alerts',
                    'Angalia na ushughulishe taarifa za WhatsApp kwa arifa za bei kwa mkono'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={checkAlerts} 
                  variant="outline"
                  className="w-full"
                  data-testid="check-alerts-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {t('Check & Send Notifications', 'Angalia na Tuma Taarifa')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notification History Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {t('Notification History', 'Historia ya Taarifa')}
                </CardTitle>
                <CardDescription>
                  {t('Recent WhatsApp notifications sent for your alerts', 'Taarifa za hivi karibuni za WhatsApp zilizotumwa kwa arifa zako')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingNotifications ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('No notifications yet', 'Hakuna taarifa bado')}</p>
                    <p className="text-sm">
                      {t('WhatsApp notifications will appear here when price alerts are triggered', 
                         'Taarifa za WhatsApp zitaonekana hapa arifa za bei zinaposhughulikiwa')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notification, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                      >
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          notification.status === 'sent' ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{notification.stock_symbol}</span>
                            <Badge variant="outline" className="text-xs">
                              {notification.condition === 'above' ? '↑' : '↓'} TZS {notification.target_price?.toLocaleString()}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t('Current price:', 'Bei ya sasa:')} TZS {notification.current_price?.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant={notification.status === 'sent' ? 'default' : 'destructive'}>
                          {notification.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
